/**
 * The countries and currencies the "Cấu hình công ty" screen offers — Task CS-2.
 *
 * ── Why a curated list and not every ISO code ───────────────────────────────
 *
 * The **server** stores a shape, not a vocabulary: migration 067 CHECKs
 * `^[A-Z]{2}$` and `^[A-Z]{3}$` and deliberately holds no enum, because ISO 3166-1
 * and ISO 4217 change and a stale enum refuses a legitimate company. This file is
 * the opposite thing and must not be mistaken for it: it is the **menu**, and a menu
 * of 249 countries is not a choice anybody makes well. It offers the places this
 * product is sold into, in the reader's own language, and the server stays the
 * authority on what is acceptable — a company somewhere not listed here is a line
 * added to this array, never a loosening anywhere else.
 *
 * ── The names live here rather than in `messages.ts` ────────────────────────
 *
 * Twenty country names in two catalogues would be forty keys whose only reader is
 * one `<option>`, and a key per proper noun is the kind of indirection that makes
 * the list harder to correct, not easier. The two names sit on the row they name.
 * Currencies carry no name at all: the ISO code is what a payslip prints and what
 * the person is choosing, and "VND — đồng Việt Nam" would put a translation next to
 * a value that has none.
 */

export interface Country {
  /** ISO 3166-1 alpha-2, upper case — exactly what the `PUT` body carries. */
  readonly code: string;
  readonly vi: string;
  readonly en: string;
}

/**
 * Vietnam first, then the region this product sells into, then the countries a
 * Vietnamese company most often has a parent or a client in. Not alphabetical: the
 * first entry is the answer for almost every reader, and making them scroll past
 * Australia to reach it would be alphabetising for its own sake.
 */
export const COUNTRIES: readonly Country[] = [
  { code: 'VN', vi: 'Việt Nam', en: 'Vietnam' },
  { code: 'TH', vi: 'Thái Lan', en: 'Thailand' },
  { code: 'ID', vi: 'Indonesia', en: 'Indonesia' },
  { code: 'MY', vi: 'Malaysia', en: 'Malaysia' },
  { code: 'PH', vi: 'Philippines', en: 'Philippines' },
  { code: 'SG', vi: 'Singapore', en: 'Singapore' },
  { code: 'KH', vi: 'Campuchia', en: 'Cambodia' },
  { code: 'LA', vi: 'Lào', en: 'Laos' },
  { code: 'MM', vi: 'Myanmar', en: 'Myanmar' },
  { code: 'KR', vi: 'Hàn Quốc', en: 'South Korea' },
  { code: 'JP', vi: 'Nhật Bản', en: 'Japan' },
  { code: 'CN', vi: 'Trung Quốc', en: 'China' },
  { code: 'TW', vi: 'Đài Loan', en: 'Taiwan' },
  { code: 'HK', vi: 'Hồng Kông', en: 'Hong Kong' },
  { code: 'IN', vi: 'Ấn Độ', en: 'India' },
  { code: 'AU', vi: 'Úc', en: 'Australia' },
  { code: 'US', vi: 'Hoa Kỳ', en: 'United States' },
  { code: 'GB', vi: 'Anh', en: 'United Kingdom' },
  { code: 'DE', vi: 'Đức', en: 'Germany' },
  { code: 'FR', vi: 'Pháp', en: 'France' },
];

/** ISO 4217, in the order the countries above introduce them. */
export const CURRENCIES: readonly string[] = [
  'VND',
  'THB',
  'IDR',
  'MYR',
  'PHP',
  'SGD',
  'KHR',
  'LAK',
  'MMK',
  'KRW',
  'JPY',
  'CNY',
  'TWD',
  'HKD',
  'INR',
  'AUD',
  'USD',
  'GBP',
  'EUR',
];

const DEFAULT_CURRENCY: Readonly<Record<string, string>> = {
  VN: 'VND',
  TH: 'THB',
  ID: 'IDR',
  MY: 'MYR',
  PH: 'PHP',
  SG: 'SGD',
  KH: 'KHR',
  LA: 'LAK',
  MM: 'MMK',
  KR: 'KRW',
  JP: 'JPY',
  CN: 'CNY',
  TW: 'TWD',
  HK: 'HKD',
  IN: 'INR',
  AU: 'AUD',
  US: 'USD',
  GB: 'GBP',
  DE: 'EUR',
  FR: 'EUR',
};

/**
 * The currency a country is *usually* paid in, or `null`.
 *
 * A suggestion the person may overwrite — a company in Singapore that pays in USD is
 * ordinary, and the screen re-selects only while they have not chosen for
 * themselves. `null` for anything not on the list above rather than a fallback to
 * `VND`: a guess made silently is how a company ends up labelled in đồng without
 * anyone having chosen it.
 */
export function defaultCurrencyFor(countryCode: string): string | null {
  return DEFAULT_CURRENCY[countryCode] ?? null;
}

/** The country's name in the reader's language, or the bare code when unlisted. */
export function countryName(countryCode: string, locale: string): string {
  const country = COUNTRIES.find((entry) => entry.code === countryCode);
  if (country === undefined) return countryCode;
  return locale === 'en' ? country.en : country.vi;
}

/**
 * The language a `vi-VN` / `en-US` tag stands for. The region half is dropped on
 * purpose: this row says which language the mirror writes in, and "Tiếng Việt (Việt
 * Nam)" answers a question nobody on this screen asked.
 */
export function localeName(tag: string, locale: string): string {
  const language = tag.split('-')[0]?.toLowerCase() ?? tag;
  if (language === 'vi') return locale === 'en' ? 'Vietnamese' : 'Tiếng Việt';
  if (language === 'en') return locale === 'en' ? 'English' : 'Tiếng Anh';
  return tag;
}
